import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PatientLayout, HelpDrawer, PatientLoading, PatientError } from '@/components/PatientLayout';
import { useLang } from '@/lib/i18n';
import { MapPin, FileCheck2 } from 'lucide-react';

export default function PatientLink() {
  const { t } = useLang();
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/patient/by-token/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || t('layout.linkNotFound'));
    }
  }, [token, t]);

  useEffect(() => { load(); }, [load]);

  if (error) return <PatientError message={error} />;
  if (!data) return <PatientLoading />;

  const { practice, submission } = data;
  // A form still with the patient opens straight away: the welcome screen and
  // the form's first step both meant "go", so the reassurance moved into the
  // form's header (full-flow review 2026-09-06 §2). This page is for coming back.
  if (submission.status === 'draft_patient') return <Navigate to={`/p/${token}/form`} replace />;

  return (
    <PatientLayout
      practice={practice}
      right={<HelpDrawer practice={practice} token={token} triggerTestId="patient-welcome-need-help-button" />}
      footer={
          <div className="flex gap-2.5">
            <Button
              className="flex-1 h-14 rounded-xl text-base font-semibold"
              data-testid="patient-track-status-button"
              onClick={() => navigate(`/p/${token}/status`)}
            >
              <MapPin className="h-5 w-5 mr-1.5" /> {t('welcome.track')}
            </Button>
            <Button
              variant="outline"
              className="flex-1 h-14 rounded-xl text-base"
              data-testid="patient-view-document-button"
              onClick={() => navigate(`/p/${token}/document`)}
            >
              <FileCheck2 className="h-5 w-5 mr-1.5" /> {t('welcome.myDocs')}
            </Button>
          </div>
      }
    >
      <div className="space-y-5">
        <div className="pt-2">
          <h1 className="patient-hero-title text-[32px] sm:text-4xl leading-tight font-bold" data-testid="patient-welcome-title">
            {t('welcome.titleBack')}
          </h1>
          <p className="mt-3 text-muted-foreground text-[17px] leading-7">{t('welcome.subtitleBack')}</p>
        </div>
        <Card className="rounded-2xl">
          <CardContent className="p-5 text-[15px] text-muted-foreground leading-7">
            {t('welcome.workingCard', { track: `“${t('welcome.track')}”` })}
          </CardContent>
        </Card>
      </div>
    </PatientLayout>
  );
}
